import { CustomButton, ResponsiveDialog } from "@hapinest/theme";
import { Box, hexToRgb, makeStyles, Typography } from "@material-ui/core";
import { cloneDeep } from "lodash";
import PropTypes from "prop-types";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createURLQueryFromParams } from "../../common/api/apiUtil";
import { useCurrency, useDate, useI18N, useLocation } from "../../common/common";
import { CUSTOMER_PROSPECT, RESERVATION_STATUS, TRIAL_TRIPS_MAX } from "../../common/constant";
import { calculateDays, dateToURLDateString, dateToUTCTimezone, getYearMonthDayFormat, isToday } from "../../common/date";
import { AVAILABILITY_OVERVIEW, BOOKING, CONTACT, MEMBERSHIP } from "../../common/routes";
import BuyAShareDialogWrapper from "../../component/dialog/membership/BuyAShareDialogWrapper";
import LoginTrialOrMemberDialog from "../../component/dialog/membership/LoginTrialOrMemberDialog";
import TrialTripsLimitDialog from "../../component/dialog/membership/TrialTripsLimitDialog";
import ApartmentAccessibilityPopover from "../../component/view/apartment/ApartmentAccessibilityPopover";
import ApartmentGuestInfoPopover from "../../component/view/apartment/ApartmentGuestInfoPopover";
import ApartmentReservationOptions from "../../component/view/apartment/ApartmentReservationOptions";
import ApartmentWaitingListDialog from "../../component/view/apartment/ApartmentWaitingListDialog";
import GuestsCountDialog from "../../component/view/availability-overview/GuestsCountDialog";
import ResortSelectionDialog from "../../component/view/availability-overview/ResortSelectionDialog";
import BookingErrorDialog, { bookingErrorType } from "../../component/view/booking/BookingErrorDialog";
import {
    useApartmentSearch,
    useApartmentShowMore,
    useBookingReview,
    useBreakpoints,
    useKeycloakFunctions,
    useReservations,
    useWaitingList,
} from "../../hooks";
import { getDatesFromToLabel } from "../../selectors/dates";
import { useAnalytics } from "./AnalyticsProvider";
import { ApartmentCategoryAvailabilityCalendarContext } from "./ApartmentCategoryAvailabilityCalendarProvider";
import { CurrencyContext } from "./CurrencyProvider";
import { LocaleContext } from "./LocaleProvider";
import { UserContext } from "./UserProvider";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
    textLoading: {
        whiteSpace: "nowrap",
        display: "inline-block",
        marginRight: theme.spacing(2),
        color: hexToRgb(theme.palette.text.primary, theme.palette.textOpacity.textMedium)
    },
    imgLoading: {
        whiteSpace: "nowrap",
        display: "inline-block",
        margin: "auto"
    }
}));

const AvailabilityOverviewProvider = ({children}) => {
    const {i18n} = useI18N();
    const {isExtraSmallScreen} = useBreakpoints();
    const {navigate} = useLocation();
    const {initialized, authenticated, isProspect, isAdmin, isTrialMember} = useKeycloakFunctions();
    const {sendCustomEvent,
        eventNames,
        getResortAndApartmentData,
        getApartmentItemData,
        sendEcommerceEvent,
        itemListNames,
        itemListIds
    } = useAnalytics();
    const {loadApartments, loading: loadingApartments} = useApartmentSearch();
    const {getBookingReviewData, loadingBookingReviewData} = useBookingReview();
    const {reservations, getReservations} = useReservations();
    const {getApartmentShowMore, apartmentShowMore} = useApartmentShowMore();

    const [trialMember] = useState(isTrialMember());
    const [prospectMember] = useState(isProspect());
    const [isGuestOrProspect, setGuestOrProspect] = useState(false);
    const [accessibilityPopoverAnchorEl, setAccessibilityPopoverAnchorEl] = useState(null);
    const [guestInfoPopoverAnchorEl, setGuestInfoPopoverAnchorEl] = useState(null);
    const [isDestinationDialogOpen, setDestinationDialogOpen] = useState(false);
    const [isGuestsDialogOpen, setGuestsDialogOpen] = useState(false);
    const [selectedApartmentCategory, setSelectedApartmentCategory] = useState();
    const [selectedResort, setSelectedResort] = useState();
    const [selectedGuestsCount, setSelectedGuestsCount] = useState(2);
    const [selectedWheelChairOption, setSelectedWheelChairOption] = useState(false);
    const [maxNumberOfGuestsOfResort, setMaxNumberOfGuestsOfResort] = useState(0);
    const [isWheelchairAccessibilityEnabled, setIsWheelchairAccessibilityEnabled] = useState(false);
    const [isWaitingListDialogOpen, setWaitingListDialogOpen] = useState(false);
    const [cannotEnterReason, setCannotEnterReason] = useState(null);
    const [isOptionsDialogOpen, setOptionsDialogOpen] = useState(false);
    const [bookingErrorDialogType, setBookingErrorDialogType] = useState();
    const [isLoginTrialOrMemberDialogOpen, setLoginTrialOrMemberDialogOpen] = useState(false);
    const [isTrialTripsDialogOpen, setIsTrialTripsDialogOpen] = useState(false);
    const [isBuySharesDialogOpen, setIsBuySharesDialogOpen] = useState(false);
    const [regularOptionApartment, setRegularOptionApartment] = useState();
    const [flexOptionApartment, setFlexOptionApartment] = useState();
    const [isLoadingView, setLoadingView] = useState(false);
    const [selectedCalendarCategoryApartment, setSelectedCalendarCategoryApartment] = useState();
    const [selectedApartmentCategoryIndex, setSelectedApartmentCategoryIndex] = useState();

    const displayCurrency = useContext(CurrencyContext)?.[0]?.currency;
    const userCurrency = useContext(UserContext)?.[0]?.user?.currency;
    const {localeUppercase, localeUppercaseUnderscore} = useContext(LocaleContext)[0];
    const {
        dateFrom,
        dateTo,
        handleOpenSnackbar,
    } = useContext(ApartmentCategoryAvailabilityCalendarContext);

    const {formatCurrency} = useCurrency();
    const {
        getCanEnterWaitingList,
        loadingCanEnterWaitingList,
        createWaitingList,
        createWaitingListLoading,
        newWaitingList,
        createWaitingListError,
    } = useWaitingList();
    const classes = useStyles();
    const {formatDate} = useDate();

    const datesFromToLabel = useMemo(() => {
        if (dateFrom && dateTo) {
            return getDatesFromToLabel(dateFrom, dateTo, "", formatDate, true);
        }
    }, [dateFrom, dateTo]);

    const numberOfNights = useMemo(() => {
        if (dateFrom && dateTo) {
            return calculateDays(dateToUTCTimezone(dateFrom), dateToUTCTimezone(dateTo));
        }
    }, [dateFrom, dateTo]);

    const actionButton = (onClick, text, variant, color, loading, disabled) => {
        return (
            <CustomButton
                fullWidth={isExtraSmallScreen()}
                variant={variant}
                color={color}
                disableElevation
                size={isExtraSmallScreen() ? "large" : "medium"}
                onClick={onClick}
                loading={loading}
                disabled={disabled}
            >
                {text}
            </CustomButton>
        );
    };

    const getActionButtonWithText = (apartment, index, loadApartmentWithPricesLoading, trialMember) => {
        if (!selectedResort?.isOnlineBookable) {
            // Not online bookable
            return {
                text: <Typography>{i18n("view.apartment_card.online_bookable.title")}</Typography>,
                button: actionButton(() => navigate(CONTACT), i18n("view.apartment_card.online_bookable.cta"), "outlined", "black"),
            };
        } else if (trialMember && (selectedResort?.houseboat || selectedResort?.isPartner)) {
            // Trial members can not book houseboats or partner resorts
            return {
                text: <Typography>{i18n("view.apartment_card.property_available_only_shareholders")}</Typography>,
                button: actionButton(() => navigate(MEMBERSHIP), i18n("common.label.learn_more"), "outlined", "black"),
            };
        }

        if (!dateFrom || !dateTo) {
            return {
                text: <Typography>{i18n("view.resort.apartment_category_availability_dialog.select_dates_to_see_prices")}</Typography>,
                button: actionButton(() => handleReserveButtonClick(apartment, index), i18n("common.label.reserve"), "contained", "primary", apartment?.isLoading, loadApartmentWithPricesLoading && !apartment?.isLoading)
            };
        }

        if (loadApartmentWithPricesLoading) {
            return {
                text: (
                    <>
                        <Box
                            display={"flex"}
                        >
                            <Typography
                                className={classes.textLoading}
                            >
                                {i18n("view.search.search_resort.calculate_price")}

                            </Typography>
                            <img
                                src={"/images/simple_loading.gif"}
                                width={32}
                                height={24}
                                className={classes.imgLoading}
                                alt={"Loading Dots"}
                            />

                        </Box>
                    </>
                ),
                button: actionButton(() => handleReserveButtonClick(apartment, index), i18n("common.label.reserve"), "contained", "primary", apartment?.isLoading, loadApartmentWithPricesLoading && !apartment?.isLoading)
            };
        }

        if (!apartment) {
            return {
                text: <Typography>{i18n("view.resort.apartment_category_availability_dialog.select_dates_to_see_prices")}</Typography>,
                button: actionButton(() => handleReserveButtonClick(apartment, index), i18n("common.label.reserve"), "contained", "primary", apartment?.isLoading, loadApartmentWithPricesLoading && !apartment?.isLoading)
            };
        }

        if (apartment?.available === true) {
            // Available
            let price = apartment.local_costs;
            if (trialMember && apartment.trial_booking_costs) {
                price += apartment.trial_booking_costs;
            }

            let extraBedText = "";
            if (apartment?.extra_bed) {
                if (selectedGuestsCount === apartment?.max_number_of_guests) {
                    extraBedText = i18n("view.apartment_card.extra_bed");
                } else if (apartment?.max_number_of_guests > selectedGuestsCount) {
                    extraBedText = i18n("view.apartment_card.optional_extra");
                }
            }

            return {
                text: (
                    <>
                        <Typography>{`${numberOfNights} ${numberOfNights > 1 ? i18n("common.label.nights") : i18n("common.label.night")}`}{extraBedText ? `, ${extraBedText}` : ""}</Typography>
                        <Typography>
                            <b>
                                {!trialMember && <>{apartment.total_points}&nbsp;{i18n("common.label.points") + " "}</>}
                                <Box
                                    component="span"
                                    whiteSpace="nowrap"
                                >
                                    {formatCurrency(apartment.currency, price)}
                                </Box>
                            </b>
                        </Typography>
                    </>
                ),
                button: actionButton(() => handleReserveButtonClick(apartment, index), i18n("common.label.reserve"), "contained", "primary", apartment?.isLoading, loadApartmentWithPricesLoading && !apartment?.isLoading)
            };
        } else {
            // Not available
            if (!authenticated || trialMember || prospectMember) {
                return {
                    text: (
                        <>
                            <Typography>{datesFromToLabel}</Typography>
                            <Typography><b>{i18n("view.availability_overview_compare_view.no_availability_adjust_your_dates")}</b></Typography>
                        </>
                    ),
                };
            } else {
                return {
                    text: (
                        <>
                            <Typography>{`${numberOfNights} ${numberOfNights > 1 ? i18n("common.label.nights") : i18n("common.label.night")}`}</Typography>
                            <Typography><b>{datesFromToLabel}</b></Typography>
                        </>
                    ),
                    button: actionButton(() => handleJoinWaitingList(apartment, selectedResort), i18n("view.apartment_card.alternatives.waiting_list.cta"), "outlined", "black", (loadingCanEnterWaitingList || createWaitingListLoading) && selectedApartmentCategory === apartment, (loadingCanEnterWaitingList || createWaitingListLoading) && selectedApartmentCategory !== apartment)
                };
            }
        }
    };

    useEffect(() => {
        if (createWaitingListError) {
            return handleOpenSnackbar(i18n("common.label.generic_error"), "", "warning");
        } else if (newWaitingList && !createWaitingListLoading) {
            setCannotEnterReason(null);
            handleOpenWaitingListDialog();

            // Send Add to Wishlist GA event
            if (selectedApartmentCategory) {
                const data = {
                    points_no: 0,
                    items: [getApartmentItemData(selectedResort, selectedApartmentCategory, 0, displayCurrency, {
                        ...getTrackingSearchData(selectedApartmentCategory, selectedApartmentCategoryIndex),
                    })]
                };
                sendEcommerceEvent(eventNames?.ADD_TO_WISHLIST, data);
            }
        }
    }, [newWaitingList, createWaitingListLoading, createWaitingListError]);

    useEffect(() => {
        if (!authenticated || prospectMember) {
            setGuestOrProspect(true);
        }
    }, [initialized]);

    useEffect(() => {
        if (trialMember) {
            getReservations([RESERVATION_STATUS.UPCOMING, RESERVATION_STATUS.PAST]);
        }
    }, [trialMember]);

    useEffect(() => {
        if (!selectedCalendarCategoryApartment || !dateTo || !dateFrom || !sendCustomEvent) {
            return;
        }

        sendCustomEvent({
            event_name: eventNames.UPDATE_DATES,
            [eventNames.UPDATE_DATES]: getResortAndApartmentData(selectedResort, selectedCalendarCategoryApartment, selectedGuestsCount)
        });
    }, [selectedCalendarCategoryApartment, dateTo, dateFrom, sendCustomEvent]);

    const handleOpenGuestsSelectionDialog = () => {
        setGuestsDialogOpen(true);
    };

    const handleCloseGuestsSelectionDialog = () => {
        setGuestsDialogOpen(false);
    };

    const handleOpenDestinationDialog = () => {
        setDestinationDialogOpen(true);
    };

    const handleCloseDestinationDialog = () => {
        setDestinationDialogOpen(false);
    };

    const handleOpenWaitingListDialog = () => {
        setWaitingListDialogOpen(true);
    };

    const handleCloseWaitingListDialog = () => {
        setWaitingListDialogOpen(false);
    };

    const handleOpenBookingErrorDialog = (type) => {
        setBookingErrorDialogType(type);
    };

    const handleCloseBookingErrorDialog = () => {
        setBookingErrorDialogType(null);
    };

    const handleCloseTrialTripsDialog = () => {
        sendCustomEvent({
            event_name: eventNames.THREE_BOOKING_POPUP_CLOSE
        });
        setIsTrialTripsDialogOpen(false);
    };

    const handleOpenBuySharesDialog = () => {
        sendCustomEvent({
            event_name: eventNames.THREE_BOOKING_POPUP_BEMEMBER
        });
        setIsTrialTripsDialogOpen(false);
        setIsBuySharesDialogOpen(true);
    };

    const handleCloseBuySharesDialog = () => {
        setIsBuySharesDialogOpen(false);
        setIsTrialTripsDialogOpen(true);
    };

    const handleResortSelected = (resortId) => {
        handleCloseDestinationDialog();
        navigate(`${AVAILABILITY_OVERVIEW}/${resortId}`);
    };

    const handleToggleAccessibility = (open, apartment) => {
        if (open && apartment) {
            getApartmentShowMore({
                variables: {
                    apartmentShowMoreId: apartment.contentful_entry_id,
                    locale: localeUppercase
                }
            });
            setAccessibilityPopoverAnchorEl(document.getElementById("accessibility_popover_anchor" + apartment.apartment_short_code));
        } else if (open === false) {
            setAccessibilityPopoverAnchorEl(null);
        }
    };

    const handleOpenGuestInfo = (apartment) => {
        if (apartment?.guest_info) {
            setSelectedApartmentCategory(apartment);
            setGuestInfoPopoverAnchorEl(document.getElementById("guestinfo_popover_anchor" + apartment.apartment_short_code));
        }
    };

    const handleCloseGuestInfo = () => {
        setSelectedApartmentCategory();
        setGuestInfoPopoverAnchorEl(null);
    };

    const handleAppliedGuestCountChanges = ({guestsCount, wheelChairValue}) => {
        setSelectedGuestsCount(guestsCount);
        setSelectedWheelChairOption(wheelChairValue);
        handleCloseGuestsSelectionDialog();

        if (sendCustomEvent) {
            sendCustomEvent({
                event_name: eventNames.UPDATE_GUESTS_NO,
                [eventNames.UPDATE_GUESTS_NO]: {
                    ...getResortAndApartmentData(selectedResort),
                    guests_no: guestsCount,
                    wheelchair: wheelChairValue
                }
            });
        }
    };

    const handleSetMaxNumberOfGuestsOfResort = (val) => {
        setMaxNumberOfGuestsOfResort(val);
    };

    const handleSetIsWheelchairAccessibilityEnabled = (val) => {
        setIsWheelchairAccessibilityEnabled(val);
    };

    const handleClearState = () => {
        setSelectedGuestsCount(2);
        setSelectedWheelChairOption(false);
        setIsWheelchairAccessibilityEnabled(false);
        setMaxNumberOfGuestsOfResort(0);
        setSelectedResort();
        setSelectedCalendarCategoryApartment();
    };

    const handleCloseReserveOptionsDialog = () => {
        setOptionsDialogOpen(false);
        setRegularOptionApartment();
        setFlexOptionApartment();
    };

    const handleReserveButtonClick = (apartment, index) => {
        if (!dateFrom || !dateTo) {
            handleOpenSnackbar(i18n("view.resort.apartment_category_availability_dialog.select_your_travel_dates_to_continue"));
            return;
        }
        handlePresentReserveOptions(apartment, index);
    };

    const handlePresentReserveOptions = (apartment, index) => {
        if (isGuestOrProspect) {
            return setLoginTrialOrMemberDialogOpen(true);
        } else if (isTrialMember && reservations?.length >= TRIAL_TRIPS_MAX) {
            return setIsTrialTripsDialogOpen(true);
        }

        // Check if authenticated
        if (initialized && !authenticated) {
            return handleOpenBookingErrorDialog(bookingErrorType.IsUnauthorized);
        }

        // Check if admin
        if (isAdmin()) {
            return handleOpenBookingErrorDialog(bookingErrorType.IsAdmin);
        }

        // Check if same day
        if (isToday(new Date(dateFrom))) {
            return handleOpenBookingErrorDialog(bookingErrorType.ArrivingToday);
        }

        setSelectedApartmentCategory(apartment);
        setSelectedApartmentCategoryIndex(index);

        const regularApartment = cloneDeep(apartment);

        // Send Add to Cart GA event
        const data = {
            points_no: apartment.total_points ? apartment.total_points : (apartment.high_season_points + apartment.low_season_points),
            items: [getApartmentItemData(selectedResort, apartment, apartment.local_costs + apartment.trial_booking_costs + (6 * apartment.total_points), displayCurrency, {
                ...getTrackingSearchData(apartment, index),
            })]
        };
        sendEcommerceEvent(eventNames?.ADD_TO_CART, data);

        // Show the loader on the button clicked
        apartment.isLoading = true;

        // Handle apartment's availability and shows the flex option
        loadApartments({
            resort_id: selectedResort?.hapimagResortId,
            apartment_short_code: regularApartment?.apartments_short_code,
            date_from: dateFrom,
            date_to: dateTo,
            wheelchair_accessible: selectedWheelChairOption ? "true" : "false",
            number_of_guests: selectedGuestsCount,
            currency: displayCurrency,
            locale: localeUppercaseUnderscore,
            flex: true, // this should always be true, so if there is no available flex option, we get available===false in the response
        })
            .then((apartmentRes) => {
                apartment.isLoading = false;
                if (apartmentRes?.apartments?.length) {
                    const flexApartment = apartmentRes.apartments[0];

                    if (flexApartment?.available) {
                        flexApartment.date_from = regularApartment?.date_from;
                        flexApartment.date_to = regularApartment?.date_to;
                        setFlexOptionApartment(flexApartment);
                    } else {
                        setFlexOptionApartment(null);
                    }

                    if (!apartment?.apartment_short_code) {
                        regularApartment.cancellation_policies = flexApartment.cancellation_policies;
                    }

                    setRegularOptionApartment(regularApartment);
                    setOptionsDialogOpen(true);
                } else {
                    // if we don't get apartment results, something is wrong
                    handleCloseReserveOptionsDialog();
                    return handleOpenBookingErrorDialog(bookingErrorType.UnknownError);
                }
            })
            .catch(() => {
                apartment.isLoading = false;
                handleCloseReserveOptionsDialog();
                return handleOpenBookingErrorDialog(bookingErrorType.UnknownError);
            });
    };

    const handleReserveApartment = (apartment, isFlexOption) => {
        // Check if same day
        if (isToday(new Date(apartment.date_from || dateFrom))) {
            handleCloseReserveOptionsDialog();
            return handleOpenBookingErrorDialog(bookingErrorType.ArrivingToday);
        }

        // Show the loader on the button clicked
        apartment.isLoading = true;

        // use the user currency for the booking. if no currency is set, use the display currency
        const currency = userCurrency || displayCurrency;

        sendBeginCheckoutEvent(apartment, isFlexOption);

        // Handle apartment's availability
        loadApartments({
            resort_id: selectedResort?.hapimagResortId,
            apartment_short_code: apartment.apartment_short_code,
            date_from: dateFrom,
            date_to: dateTo,
            wheelchair_accessible: selectedWheelChairOption ? "true" : "false",
            number_of_guests: selectedGuestsCount,
            flex: isFlexOption,
            locale: localeUppercaseUnderscore,
        }, true, currency)
            .then((apartmentRes) => {
                if (apartmentRes && apartmentRes?.apartments?.length) {
                    const apartmentToReserve = apartmentRes.apartments[0];

                    if (apartmentToReserve?.available) {
                        // Handle Apartment's booking rights
                        const from = dateToURLDateString(dateToUTCTimezone(dateFrom));
                        const to = dateToURLDateString(dateToUTCTimezone(dateTo));
                        getBookingReviewData(
                            selectedResort?.hapimagResortId,
                            apartmentToReserve?.apartment_short_code,
                            from,
                            to,
                            apartmentToReserve?.high_season_points,
                            apartmentToReserve?.low_season_points,
                            localeUppercaseUnderscore,
                            currency
                        )
                            .then(reviewRes => {
                                apartment.isLoading = false;

                                const navPayload = {
                                    bookingReviewData: reviewRes,
                                    selectedResort,
                                    apartment: apartmentToReserve
                                };

                                let urlParams = {};
                                urlParams.resort_slug = selectedResort?.slug;
                                urlParams.apartment_short_code = apartmentToReserve?.apartment_short_code;
                                urlParams.date_from = from;
                                urlParams.date_to = to;
                                urlParams.number_of_guests = selectedGuestsCount;
                                urlParams.wheelchair_accessible = selectedWheelChairOption ? "true" : "false";
                                urlParams.from_availability_overview = "true";

                                if (from !== dateFrom) {
                                    urlParams.search_date_from = dateFrom;
                                }
                                if (to !== dateTo) {
                                    urlParams.search_date_to = dateTo;
                                }

                                if (isFlexOption) {
                                    urlParams.flex_option = isFlexOption;
                                }

                                navigate(BOOKING + "/" + createURLQueryFromParams(urlParams), navPayload, false, false);
                            })
                            .catch(error => {
                                apartment.isLoading = false;
                                handleCloseReserveOptionsDialog();

                                if (error?.response) {
                                    // show error by status
                                    const {status, data} = error?.response;

                                    // Handle prospect forbidden
                                    if (status === 403 && data?.detail === CUSTOMER_PROSPECT) {
                                        return handleOpenBookingErrorDialog(bookingErrorType.ProspectForbidden);
                                    }

                                    if (status === 412) {
                                        return handleOpenBookingErrorDialog(bookingErrorType[data?.detail[0]?.type]);
                                    }
                                }
                                // present a generic error modal
                                return handleOpenBookingErrorDialog(bookingErrorType.UnknownError);
                            });
                    } else {
                        // Handle apartment not available
                        apartment.isLoading = false;
                        // deactivate apartment from list
                        selectedApartmentCategory.available = false;
                        selectedApartmentCategory.no_of_available_apartments = 0;
                        handleCloseReserveOptionsDialog();
                        return handleOpenBookingErrorDialog(bookingErrorType.ApartmentNotAvailable);
                    }
                } else {
                    // if we don't have apartment results, something is wrong
                    apartment.isLoading = false;
                    handleCloseReserveOptionsDialog();
                    return handleOpenBookingErrorDialog(bookingErrorType.UnknownError);
                }
            })
            .catch(() => {
                apartment.isLoading = false;
                handleCloseReserveOptionsDialog();
                return handleOpenBookingErrorDialog(bookingErrorType.UnknownError);
            });
    };

    const handleJoinWaitingList = (apartment) => {
        if (apartment) {
            setSelectedApartmentCategory(apartment);
            getCanEnterWaitingList(
                selectedResort?.hapimagResortId,
                apartment?.apartment_short_code,
                dateFrom,
                dateTo,
                selectedGuestsCount,
                numberOfNights,
            ).then(data => {
                const canEnterWaitingList = data?.apartments[apartment?.apartment_short_code];
                if (canEnterWaitingList) {
                    if (canEnterWaitingList.can_enter) {
                        createWaitingList(
                            selectedResort?.hapimagResortId,
                            apartment?.apartment_short_code,
                            dateFrom,
                            dateTo,
                            selectedGuestsCount,
                            numberOfNights
                        );
                    } else {
                        sendCustomEvent({
                            event_name: eventNames.JOIN_WAITINGLIST_FAILED,
                            [eventNames.JOIN_WAITINGLIST_FAILED]: {
                                ...getResortAndApartmentData(selectedResort, apartment, selectedGuestsCount),
                                error_code: canEnterWaitingList?.reason
                            }
                        });
                        setCannotEnterReason(canEnterWaitingList?.reason);
                        handleOpenWaitingListDialog();
                    }
                } else {
                    throw new Error("Error getting waiting list can enter");
                }
            })
                .catch(() => {
                    handleOpenSnackbar(i18n("common.label.generic_error"), "", "warning");
                });

            sendCustomEvent({
                event_name: eventNames.WAITING_LIST_JOIN_BTN,
                [eventNames.WAITING_LIST_JOIN_BTN]: getResortAndApartmentData(selectedResort, apartment, selectedGuestsCount)
            });
        }
    };

    const getTrackingSearchData = useCallback((apartment, index) => {
        return {
            item_list_name: itemListNames.AVAILABILITY_CALENDAR_LIST,
            item_list_id: itemListIds.AVAILABILITY_CALENDAR,
            item_nights: numberOfNights ? numberOfNights : undefined,
            item_date_flexibility: false,
            item_date_in: dateFrom ? getYearMonthDayFormat(moment(dateFrom)) : undefined,
            item_date_out: dateTo ? getYearMonthDayFormat(moment(dateTo)) : undefined,
            item_availability: apartment?.available ? "Available" : "Unavailable",
            index: index + 1,
        };
    }, [numberOfNights, dateFrom, dateTo]);

    const sendBeginCheckoutEvent = (apartment, flexOption) => {
        const price = apartment.local_costs + apartment.trial_booking_costs + (6 * apartment.total_points);
        const data = {
            points_no: apartment.total_points,
            refundable: `${flexOption}`,
            value: price,
            currency: displayCurrency,
            entry_point: "search with fix dates",
            items: [getApartmentItemData(selectedResort, apartment, price, displayCurrency, {
                ...getTrackingSearchData(apartment, selectedApartmentCategoryIndex),
            })]
        };
        sendEcommerceEvent(eventNames?.BEGIN_CHECKOUT, data);
    };

    const handleSetCalendarApartmentCategory = (apartment) => {
        setSelectedCalendarCategoryApartment(apartment);
    };

    return (
        <AvailabilityOverviewContext.Provider value={{
            isDestinationDialogOpen,
            isGuestsDialogOpen,
            selectedApartmentCategory,
            selectedGuestsCount,
            selectedWheelChairOption,
            getActionButtonWithText,
            selectedResort,
            setSelectedResort,
            getTrackingSearchData,
            selectedCalendarCategoryApartment,
            handleOpenGuestsSelectionDialog,
            handleCloseGuestsSelectionDialog,
            handleOpenDestinationDialog,
            handleCloseDestinationDialog,
            handleToggleAccessibility,
            handleOpenGuestInfo,
            handleCloseGuestInfo,
            handleReserveButtonClick,
            handleSetMaxNumberOfGuestsOfResort,
            handleSetIsWheelchairAccessibilityEnabled,
            handleClearState,
            handleSetCalendarApartmentCategory
        }}
        >
            {children}

            {accessibilityPopoverAnchorEl && (
                <ApartmentAccessibilityPopover
                    anchorEl={accessibilityPopoverAnchorEl}
                    onClose={handleToggleAccessibility}
                    apartmentCategoryAccessibilityItems={apartmentShowMore?.apartmentCategoryAccessibilityCollection?.items}
                />
            )}

            {guestInfoPopoverAnchorEl && (
                <ApartmentGuestInfoPopover
                    anchorEl={guestInfoPopoverAnchorEl}
                    onClose={handleCloseGuestInfo}
                    apartmentCategoryGuestInfo={selectedApartmentCategory?.guest_info}
                />
            )}

            {isDestinationDialogOpen && (
                <ResortSelectionDialog
                    open={isDestinationDialogOpen}
                    onClose={handleCloseDestinationDialog}
                    onResortSelected={handleResortSelected}
                    resortId={selectedResort?.hapimagResortId}
                />
            )}

            {isGuestsDialogOpen && (
                <GuestsCountDialog
                    open={isGuestsDialogOpen}
                    onClose={handleCloseGuestsSelectionDialog}
                    onApplyChanges={handleAppliedGuestCountChanges}
                    numberOfGuests={selectedGuestsCount}
                    maxGuests={maxNumberOfGuestsOfResort}
                    wheelChair={selectedWheelChairOption}
                    isWheelchairAccessibilityEnabled={isWheelchairAccessibilityEnabled}
                />
            )}

            {isWaitingListDialogOpen && (
                <ApartmentWaitingListDialog
                    isOpen={isWaitingListDialogOpen}
                    onClose={handleCloseWaitingListDialog}
                    resortName={selectedResort?.resortName}
                    expirationDaysBeforeArrival={selectedResort?.expirationDaysBeforeArrival}
                    cannotEnterReason={cannotEnterReason}
                />
            )}
            {isGuestOrProspect &&
                <LoginTrialOrMemberDialog
                    isOpen={isLoginTrialOrMemberDialogOpen}
                    onClose={() => setLoginTrialOrMemberDialogOpen(false)}
                />
            }

            {isTrialMember && reservations?.length >= TRIAL_TRIPS_MAX &&
                <>
                    <TrialTripsLimitDialog
                        isOpen={isTrialTripsDialogOpen}
                        onClose={handleCloseTrialTripsDialog}
                        disableBackdropClick={false}
                        hasCloseButton
                        onFirstAction={handleOpenBuySharesDialog}
                        onSecondAction={() => navigate(MEMBERSHIP)}
                    />
                    <BuyAShareDialogWrapper
                        isOpen={isBuySharesDialogOpen}
                        onClose={handleCloseBuySharesDialog}
                    />
                </>
            }

            {bookingErrorDialogType && (
                <BookingErrorDialog
                    isOpen={bookingErrorDialogType !== undefined}
                    type={bookingErrorDialogType}
                    onClose={handleCloseBookingErrorDialog}
                    resortPhoneNumber={selectedResort?.resortContactDetails?.resortPhoneNumber}
                    buttonText={"common.label.close"}
                />
            )}

            <ResponsiveDialog
                open={isOptionsDialogOpen}
                disableBackdropClick
                handleClose={handleCloseReserveOptionsDialog}
                maxWidth="sm"
                hasCloseButton
                content={(
                    <Box
                        pt={isExtraSmallScreen() ? 0 : 1}
                        pb={2}
                    >
                        <ApartmentReservationOptions
                            apartment={regularOptionApartment}
                            apartmentFlex={flexOptionApartment}
                            dateFrom={regularOptionApartment?.date_from || dateFrom}
                            dateTo={regularOptionApartment?.date_to || dateTo}
                            nights={regularOptionApartment?.number_of_nights || numberOfNights}
                            onReserveOption={handleReserveApartment}
                            reserveOptionDisabled={(loadingApartments || loadingBookingReviewData) && !flexOptionApartment?.isLoading}
                            isTrialMember={trialMember}
                            resort={selectedResort}
                        />
                    </Box>
                )}
                onEntered={() => isLoadingView && setLoadingView(false)}
            />
        </AvailabilityOverviewContext.Provider>
    );
};

AvailabilityOverviewProvider.propTypes = {
    children: PropTypes.any
};

export const AvailabilityOverviewContext = createContext();
export default AvailabilityOverviewProvider;
